import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { BasisSpark, Mini, MiniTable } from "./Shared";
import { LEDGER, STOCK } from "./data";

export const STATE = {
  "SCORE INPUT": "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  "GATE MET": "bg-sky-500/15 text-sky-300 border-sky-500/40",
  CONTEXT: "bg-muted text-muted-foreground border-border",
  CONDITIONAL: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  EVENT: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
};

const Evidence = ({ children }) => (
  <p className="text-[10px] text-muted-foreground border-t border-border/40 pt-2">EVIDENCE — {children}</p>
);

const cohortRows = LEDGER.filter((r) => !r.older).slice(0, 6).map((r) => [
  r.fund, r.type, r.pos, r.book, `${r.hit}%`, `${r.skill}th`, Object.entries(r.q).map(([q, a]) => `${a === "N" ? "NEW" : "ADD"} ${q}`).join(" · "),
]);

export const FACTORS = [
  { id: "f1", name: "F1 · BUYER QUALITY", short: "Buyer quality", state: "CONTEXT", value: "2 elite / 9 buyers", pct: "74th pct",
    def: "Skill composition of the funds currently initiating or adding",
    sub: "two buyers rank top-decile on 12m sector hit rate",
    detail: (
      <div className="space-y-3">
        <MiniTable testid="f1-funds" cols={["Fund", "Type", "Position", "% of book", "Sector hit 12m", "Skill pct", "Action"]} rows={cohortRows} />
        <div className="grid grid-cols-3 gap-3">
          <Mini k="Mean buyer skill" v="68th pct" /><Mini k="Top-decile buyers" v="2" /><Mini k="Buyer type mix" v="9 HF" />
        </div>
        <Evidence>skill validated at fund level (hit-rate persistence); as an unconditional stock-ranking factor it carries
          no alpha (t ≤ 0.5) → context only, excluded from score.</Evidence>
      </div>
    ) },
  { id: "f2", name: "F2 · FRESH CONVICTION", short: "Fresh conviction", state: "GATE MET", value: "9 new funds / 2q", pct: "82nd pct",
    def: "Breadth of newly disclosed institutional positions (cohort formation)",
    sub: "cohort gates met → V3 band S8 fires on this name",
    detail: (
      <div className="space-y-3">
        <MiniTable testid="f2-cohorts" cols={["Quarter", "New buyers", "Median reveal", "vs sector avg"]}
          rows={[["2026 Q2", "6", "Aug 12", "+2.6x"], ["2026 Q1", "3", "May 09", "+1.3x"], ["2025 Q4", "0", "—", "—"], ["2025 Q3", "1", "Nov 10", "0.4x"]]} />
        <div className="grid grid-cols-3 gap-3">
          <Mini k="V3 band" v="S8 / S10" accent="text-sky-300" /><Mini k="Quality gates" v="3+ buyers · bp≥0.30 · no veto" /><Mini k="Cohort percentile" v="82nd" />
        </div>
        <Evidence>inside quality gates (V3): OOS top-vs-bottom decile +16.4% (t 5.8). Unconditional breadth is
          nonstationary → score impact only via the gated V3 overlay.</Evidence>
      </div>
    ) },
  { id: "f3", name: "F3 · BASIS PREMIUM", short: "Basis premium", state: "SCORE INPUT", value: "+4.1% vs sector", pct: "72nd pct",
    def: "Price vs estimated institutional cost basis, sector-adjusted — the thesis-confirmation factor",
    sub: "pros' thesis confirming — primary score anchor", spark: true,
    detail: (
      <div className="space-y-3">
        <BasisSpark height={150} labels />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Mini k="Est. basis (build-q avg)" v={`$${STOCK.basis.toFixed(2)}`} /><Mini k="Price" v={`$${STOCK.price.toFixed(2)}`} />
          <Mini k="Raw premium" v="+7.5%" /><Mini k="Sector-adj (BP_rel)" v="+4.1%" accent="text-emerald-400" />
        </div>
        <MiniTable testid="f3-deciles" cols={["BP decile", "D1", "D3", "D5", "D7", "D10"]}
          rows={[["fwd 6m sector-adj alpha", "−2.1%", "−0.6%", "+0.4%", "+1.2%", "+1.9%"]]} />
        <Evidence>the one factor that generalizes all-equity: IC +0.050, NW-t 3.44, 82% monthly hit, positive every
          year tested (2023–26), survives Barra neutralization (resid t 2.47).</Evidence>
      </div>
    ) },
  { id: "f4", name: "F4 · SPECIALIST ALIGNMENT", short: "Specialist alignment", state: "CONTEXT", value: "centrality 0.68", pct: "68th pct",
    def: "Co-holding network centrality among elite sector specialists",
    sub: "no convergence event in progress (2 elite in, threshold 3)",
    detail: (
      <div className="space-y-3">
        <MiniTable testid="f4-specialists" cols={["Specialist holder", "Sector hit 12m", "Held since", "Cluster"]}
          rows={[["Coatue Management", "64%", "26Q2", "AI infrastructure"], ["Darsana Capital", "61%", "26Q2", "AI infrastructure"], ["Whale Rock Capital", "59%", "25Q3", "Enterprise hardware"]]} />
        <div className="grid grid-cols-3 gap-3">
          <Mini k="Convergence state" v="NOT CONVERGED" /><Mini k="Co-holding cluster" v="AI infra · 14 funds" /><Mini k="Centrality trend" v="+0.09 / 2q" />
        </div>
        <Evidence>no standalone all-equity alpha (t 0.78); validated only inside the convergence pipeline (Big Cap
          Champions) → CHAMPION badge fires when the full state is met.</Evidence>
      </div>
    ) },
  { id: "f5", name: "F5 · CROWDING", short: "Crowding", state: "CONDITIONAL", value: "312 holders", pct: "61st pct",
    def: "Institutional ownership breadth and concentration",
    sub: "normal breadth; penalty arms only in bear-vol regimes",
    detail: (
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Mini k="Holders (13F)" v="312 (61st pct)" /><Mini k="HHI concentration" v="0.048 (low)" />
          <Mini k="Top-10 holders" v="22% of inst. value" /><Mini k="Holders QoQ" v="+6%" />
        </div>
        <Evidence>breadth is neutral-to-positive in calm regimes; the crowding penalty is conditional on bear-vol
          states (sample thin — monitored, clearly labeled).</Evidence>
      </div>
    ) },
  { id: "f6", name: "F6 · SMART EXIT PRESSURE", short: "Smart exit pressure", state: "SCORE INPUT", value: "0 skilled exits / 2q", pct: "12th pct",
    def: "Skilled-holder distribution: exits and trims, skill-weighted",
    sub: "1 low-skill exit (Marshall Wace, 38th) · 1 quant trim — not counted",
    detail: (
      <div className="space-y-3">
        <MiniTable testid="f6-flags" cols={["RED-flag checklist", "Status"]}
          rows={[["Weak V3 band (S1–3)", "✗ band S8"], ["Fading 21-session trend", "✗ +2.3%"], ["Price above median street target", "✗ 14.8% upside remains"],
            ["Distribution / overcrowded regime", "✗ Compounding Drift"], ["≥ 5 sell filings in 120d", "✓ flagged (7) — common for large caps"]]} />
        <div className="grid grid-cols-3 gap-3">
          <Mini k="Skilled exits (≥80th)" v="0" /><Mini k="All exits / trims" v="1 / 1" /><Mini k="RED flags" v="1 / 5 (RED at 3)" accent="text-emerald-400" />
        </div>
        <Evidence>exit pressure: secondary score input (NW-t 2.57, positive 3 of 4 years). RED composite (≥3 flags):
          −43bp vs +73bp earnings-day drift OOS.</Evidence>
      </div>
    ) },
  { id: "f7", name: "F7 · EARNINGS SETUP", short: "Earnings setup", state: "EVENT", value: "GREEN · 12d", pct: "Nov 2",
    def: "Positioning quality into the next print (drift, not EPS prediction)",
    sub: "V3 S6–9 band + 1/5 red flags → favourable drift profile",
    detail: (
      <div className="space-y-3">
        <MiniTable testid="f7-history" cols={["Setup class (OOS)", "Avg earnings-day alpha"]}
          rows={[["Clean (0 flags)", "+73 bp"], ["1–2 flags", "+11 bp"], ["RED (3+ flags)", "−43 bp"]]} />
        <div className="grid grid-cols-2 gap-3">
          <Mini k="Last 4 prints (day move)" v="+4.1% · −1.2% · +2.8% · +0.9%" />
          <Mini k="Scope note" v="reaction drift — NOT an EPS beat predictor (that failed, IC≈0)" />
        </div>
        <Evidence>GREEN = V3 S6–9 (58.5% hit vs 54.9% EA base). The RED composite does not concentrate the &lt;−5% tail;
          we state that limitation rather than claim a miss-predictor.</Evidence>
      </div>
    ) },
];

const Chip = ({ state }) => <span className={`px-1.5 py-0.5 rounded border text-[10px] font-mono ${STATE[state]}`}>{state}</span>;

export const FactorCards = ({ initialOpen = {}, prefix = "kfm" }) => {
  const [open, setOpen] = useState(initialOpen);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start" data-testid={`${prefix}-factor-grid`}>
      {FACTORS.map((f) => (
        <Card key={f.id} data-testid={`${prefix}-factor-${f.id}`}>
          <CardContent className="pt-4 pb-4">
            <button className="w-full text-left" onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))} data-testid={`${prefix}-factor-${f.id}-toggle`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap"><span className="text-xs font-bold tracking-[0.15em]">{f.name}</span><Chip state={f.state} /></div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground">{f.pct}</span>
                  {open[f.id] ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{f.def}</div>
              <div className="flex items-end gap-3 mt-1.5">
                <span className="font-mono text-lg font-bold">{f.value}</span>
                <span className="text-[11px] text-muted-foreground flex-1">{f.sub}</span>
                {f.spark && !open[f.id] && <div className="w-40"><BasisSpark height={34} /></div>}
              </div>
            </button>
            {open[f.id] && <div className="mt-3 pt-3 border-t border-border/50" data-testid={`${prefix}-factor-${f.id}-detail`}>{f.detail}</div>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const FactorStrip = ({ prefix = "kfm" }) => {
  const [sel, setSel] = useState(null);
  const f = FACTORS.find((x) => x.id === sel);
  return (
    <div className="space-y-3" data-testid={`${prefix}-factor-strip`}>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
        {FACTORS.map((x) => (
          <button key={x.id} onClick={() => setSel(sel === x.id ? null : x.id)} data-testid={`${prefix}-strip-${x.id}`}
            className={`text-left rounded-md border px-3 py-2.5 transition-colors ${sel === x.id ? "border-sky-500/60 bg-sky-500/5" : "border-border/70 hover:border-border bg-muted/10"}`}>
            <div className="text-[9px] font-bold tracking-[0.15em] text-muted-foreground">{x.name.split(" · ")[0]} · {x.short.toUpperCase()}</div>
            <div className="font-mono text-sm font-bold mt-1 leading-tight">{x.value}</div>
            <div className="mt-1.5"><Chip state={x.state} /></div>
          </button>
        ))}
      </div>
      {f && (
        <Card data-testid={`${prefix}-strip-detail`}><CardContent className="pt-4 pb-4">
          <div className="text-xs font-bold tracking-[0.15em] mb-1">{f.name} <span className="text-muted-foreground font-normal tracking-normal">— {f.def}</span></div>
          {f.detail}
        </CardContent></Card>
      )}
    </div>
  );
};

export const FactorList = ({ prefix = "kfm", onPick, active }) => (
  <div className="space-y-1.5" data-testid={`${prefix}-factor-list`}>
    {FACTORS.map((f) => (
      <button key={f.id} onClick={() => onPick && onPick(f.id)} data-testid={`${prefix}-list-${f.id}`}
        className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${active === f.id ? "border-sky-500/60 bg-sky-500/5" : "border-border/60 hover:border-border"}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold tracking-[0.12em]">{f.name}</span>
          <span className="font-mono text-[10px] text-muted-foreground">{f.pct}</span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className="font-mono text-sm font-bold">{f.value}</span><span className="whitespace-nowrap"><Chip state={f.state} /></span>
        </div>
      </button>
    ))}
  </div>
);

export const FactorTable = ({ prefix = "kfm" }) => {
  const [open, setOpen] = useState(null);
  return (
    <Card data-testid={`${prefix}-factor-table`}><CardContent className="pt-4 pb-2">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground border-b border-border">
          <tr><th className="py-1.5 px-2 text-left">Factor</th><th className="py-1.5 px-2 text-left">Reading</th>
            <th className="py-1.5 px-2 text-left hidden md:table-cell">Interpretation</th><th className="py-1.5 px-2 text-right">Rank</th><th className="py-1.5 px-2 text-right">Role</th></tr>
        </thead>
        <tbody>
          {FACTORS.map((f) => (
            <React.Fragment key={f.id}>
              <tr className="border-b border-border/40 hover:bg-muted/30 cursor-pointer" onClick={() => setOpen(open === f.id ? null : f.id)} data-testid={`${prefix}-row-${f.id}`}>
                <td className="py-2 px-2 font-bold tracking-[0.1em] text-[11px]">{f.name}</td>
                <td className="py-2 px-2 font-mono font-bold">{f.value}</td>
                <td className="py-2 px-2 text-muted-foreground hidden md:table-cell">{f.sub}</td>
                <td className="py-2 px-2 text-right font-mono text-muted-foreground">{f.pct}</td>
                <td className="py-2 px-2 text-right"><Chip state={f.state} /></td>
              </tr>
              {open === f.id && <tr><td colSpan={5} className="px-2 py-3 bg-muted/10">{f.detail}</td></tr>}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
};
